import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../data/DataProvider';
import { pbEngineer } from '../lib/pb';
import EmptyState from '../shared/components/EmptyState';
import Skeleton from '../shared/components/Skeleton';
import { Icons } from '../shared/icons';
import SafeIcon from '../common/SafeIcon';

// Part category chips (from the seeded data; free-form beyond these).
const CATEGORIES = ['all', 'Boiler', 'Controls', 'Pumps', 'Valves', 'Radiators', 'Flues', 'Pipes'];

// Van stock lives on the engineer's OWN account record (accounts.van_stock)
// — the `engineers` collection is agency-only, but accounts is self-readable
// via `id = @request.auth.id`. Read it from the auth store and keep a local
// mirror so "+/-" adjustments update instantly.
function useVanStock() {
  const [vanStock, setVanStock] = useState(() => {
    const raw = pbEngineer.authStore.record?.van_stock;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try { return JSON.parse(raw); } catch { return []; }
    }
    return [];
  });

  const saveVanStock = async (next) => {
    const clean = Array.isArray(next) ? next : [];
    setVanStock(clean);
    try {
      await pbEngineer.collection('accounts').update(pbEngineer.authStore.record.id, {
        van_stock: JSON.stringify(clean),
      });
    } catch (err) {
      console.error('Failed to save van stock:', err);
      throw err;
    }
  };

  const adjust = async (partId, delta) => {
    const current = (Array.isArray(vanStock) ? vanStock : []).find((s) => s?.part_id === partId);
    const qty = Math.max(0, (current?.qty || 0) + delta);
    const next = [
      ...(Array.isArray(vanStock) ? vanStock : []).filter((s) => s?.part_id !== partId),
      ...(qty > 0 ? [{ part_id: partId, qty }] : []),
    ];
    await saveVanStock(next);
  };

  return { vanStock: Array.isArray(vanStock) ? vanStock : [], adjust };
}

export default function EngineerParts() {
  const { data, ready } = useData();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('all');
  const [adjustingId, setAdjustingId] = useState(null);
  const { vanStock, adjust } = useVanStock();

  const vanQty = (partId) =>
    vanStock.find((s) => s?.part_id === partId)?.qty || 0;

  const filtered = (data.parts || []).filter((part) => {
    const term = searchTerm.trim().toLowerCase();
    const matchesTerm = !term ||
      part.name.toLowerCase().includes(term) ||
      (part.part_number || '').toLowerCase().includes(term) ||
      (part.manufacturer || '').toLowerCase().includes(term) ||
      (part.category || '').toLowerCase().includes(term);
    const matchesCat = category === 'all' || (part.category || '') === category;
    return matchesTerm && matchesCat && part.status !== 'obsolete';
  });

  const handleAdjust = async (part, delta) => {
    setAdjustingId(part.id);
    try {
      await adjust(part.id, delta);
    } catch {
      // saveVanStock already logged
    } finally {
      setAdjustingId(null);
    }
  };

  if (!ready) return <div className="page-container"><Skeleton rows={6} /></div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Parts Database</h1>
        <p>Browse the parts catalogue and your van stock</p>
      </div>

      <div className="filter-bar">
        <div className="search-input">
          <SafeIcon icon={Icons.Search} size={16} />
          <input
            type="text"
            placeholder="Search parts, part numbers, manufacturers…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search parts"
          />
        </div>
      </div>

      <div className="filter-bar">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            className={`filter-button ${category === cat ? 'active' : ''}`}
            onClick={() => setCategory(cat)}
          >
            {cat.charAt(0).toUpperCase() + cat.slice(1)}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="No parts found"
          message={searchTerm ? `Nothing matches "${searchTerm}"` : 'The parts catalogue is empty'}
          icon={Icons.Package}
        />
      ) : (
        <div className="parts-grid">
          {filtered.map((part) => {
            const qty = vanQty(part.id);
            const low = qty <= part.reorder_level && part.reorder_level > 0;
            return (
              <div key={part.id} className="part-card">
                <span className="part-number">{part.part_number || '—'}</span>
                <h3>{part.name}</h3>
                <p>{part.manufacturer}{part.category ? ` · ${part.category}` : ''}</p>

                <div className="part-meta">
                  <span className={`part-stock ${low && qty > 0 ? 'low' : ''}`}>
                    {qty} in van
                  </span>
                  {part.reorder_level > 0 && (
                    <span className={`stock-badge ${low ? 'low-stock' : 'in-stock'}`}>
                      {low ? 'Running low' : 'Stock ok'}
                    </span>
                  )}
                </div>

                <div className="form-actions">
                  <button className="secondary-button" onClick={() => navigate(`${part.id}`)}>
                    <SafeIcon icon={Icons.Info} size={14} />
                    Details
                  </button>
                  <span className="form-actions" style={{ marginLeft: 'auto' }}>
                    <button
                      className="icon-button"
                      aria-label="Remove one from van"
                      title="Remove one from van"
                      disabled={adjustingId === part.id || qty <= 0}
                      onClick={() => handleAdjust(part, -1)}
                    >
                      −
                    </button>
                    <button
                      className="icon-button"
                      aria-label="Add one to van"
                      title="Add one to van"
                      disabled={adjustingId === part.id}
                      onClick={() => handleAdjust(part, 1)}
                    >
                      +
                    </button>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
